import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, Book, Bot, Video, Package } from 'lucide-react';

const DocsPage = () => {
  const { t } = useTranslation();

  const sections = [
    {
      icon: Book,
      title: t('docsPage.s1Title'),
      description: t('docsPage.s1Desc'),
      links: [
        t('docsPage.s1l1'),
        t('docsPage.s1l2'),
        t('docsPage.s1l3'),
        t('docsPage.s1l4'),
      ],
    },
    {
      icon: Bot,
      title: t('docsPage.s2Title'),
      description: t('docsPage.s2Desc'),
      links: [
        { label: t('docsPage.s2l1'), to: '/docs/chatbot#create' },
        { label: t('docsPage.s2l2'), to: '/docs/chatbot#knowledge-base' },
        { label: t('docsPage.s2l3'), to: '/docs/chatbot#website' },
        { label: t('docsPage.s2l4'), to: '/docs/chatbot#mobile' },
        { label: t('docsPage.s2l5'), to: '/docs/chatbot#api' },
        { label: t('docsPage.s2l6'), to: '/docs/chatbot#whatsapp' },
        { label: t('docsPage.s2l7'), to: '/docs/chatbot#messenger' },
        { label: t('docsPage.s2l8'), to: '/docs/chatbot#instagram' },
      ],
    },
    {
      icon: Package,
      title: t('docsPage.s3Title'),
      description: t('docsPage.s3Desc'),
      links: [
        { label: t('docsPage.s3l1'), to: '/docs/cmms#getting-started' },
        { label: t('docsPage.s3l2'), to: '/docs/cmms#assets' },
        { label: t('docsPage.s3l3'), to: '/docs/cmms#work-orders' },
        { label: t('docsPage.s3l4'), to: '/docs/cmms#pm-schedules' },
        { label: t('docsPage.s3l5'), to: '/docs/cmms#team' },
      ],
    },
    {
      icon: Video,
      title: t('docsPage.s4Title'),
      description: t('docsPage.s4Desc'),
      links: [
        t('docsPage.s4l1'),
        t('docsPage.s4l2'),
        t('docsPage.s4l3'),
        t('docsPage.s4l4'),
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Documentation | mwbTechs</title>
        <meta name="description" content="Comprehensive documentation and guides for mwbTechs products." />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">{t('docsPage.title')}</h1>
              <p className="text-xl text-gray-600">{t('docsPage.subtitle')}</p>
            </motion.div>
          </div>
        </section>

        <section className="section">
          <div className="container-custom">
            <div className="grid md:grid-cols-2 gap-8">
              {sections.map((section, index) => {
                const Icon = section.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm"
                  >
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{section.title}</h3>
                    <p className="text-gray-600 mb-6">{section.description}</p>
                    <ul className="space-y-2">
                      {section.links.map((link, i) => {
                        const isObj = typeof link === 'object';
                        const label = isObj ? link.label : link;
                        return (
                          <li key={i}>
                            {isObj ? (
                              <Link to={link.to} className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                                <ArrowRight className="w-4 h-4" />
                                {label}
                              </Link>
                            ) : (
                              <a href="#" className="text-primary-600 hover:text-primary-700 flex items-center gap-2">
                                <ArrowRight className="w-4 h-4" />
                                {label}
                              </a>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="section-sm bg-gray-50">
          <div className="container-custom text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('docsPage.needHelpTitle')}</h2>
            <p className="text-gray-600 mb-8">{t('docsPage.needHelpSubtitle')}</p>
            <div className="flex gap-4 justify-center">
              <Link to="/help" className="btn btn-primary">{t('docsPage.visitHelp')}</Link>
              <Link to="/contact" className="btn btn-outline">{t('docsPage.contactSupport')}</Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DocsPage;
